/**
 * FillerMetalTable Component
 * 
 * Renders the Filler Metals table (QW-404) for WPS PDF documents.
 * Displays filler metal specifications and thickness range qualifications.
 * 
 * Supports two table types:
 * - Standard: For Groove and Overlay welding with complete filler metal details
 * - Tubesheet: Specialized layout for tube-to-tubesheet welding procedures
 * 
 * Features:
 * - SFA specifications and classifications for up to 3 welding processes
 * - F-Number and A-Number designations
 * - Chemical analysis or trade name information
 * - Thickness range qualifications (Min/Max) with As-Welded/PWHT conditions
 * - Flux specifications and classifications
 * - Weld layer information (Overlay type only)
 * - Additional filler metal properties (Tubesheet type)
 */

const FillerMetalTable = ({ pdf, type = "Standard" }) => {
  const pdfData = pdf[0];
  const isTubesheet = type === "Tubesheet";
  const fillerfieldoverlay = [
    { type: 'Flux', prefix: 'WPSH_Flux' },
    { type: 'Sup Filler', prefix: 'WPSH_SupFiller' }

  ]
  const fillerfieldgroove = [
    { type: 'Flux', prefix: 'WPSH_Flux' },
    { type: 'Cons.Insert', prefix: 'WPSH_ConsuInsert' },
    { type: 'Sup Filler', prefix: 'WPSH_SupFiller' }
  ]

  const FillerMetal = pdf[0].wld_WPSType == "Overlay" ? fillerfieldoverlay : fillerfieldgroove

  const renderProcessRows = () => {
    const rows = [];

    for (let i = 1; i <= 3; i++) {
      if (pdf[i] !== undefined) {
        if (isTubesheet) {
          rows.push(
            <tr key={i} style={{ borderBottom: "1px solid black" }}>
              <th colSpan="2" className="WPSleft-th-nobottom" style={{ borderBottom: "1px solid black", borderRight: "none", borderLeft: "0.5px solid black", borderTop: "1px solid black" }}>
                {pdf[i]?.WPSD_Process}
              </th>
              <td className="WPSleft-td-nobottomnoleft" style={{ borderLeft: "1px solid black", textAlign: "center" }}>
                {pdf[i]?.WPSD_FM_SFA}
              </td>
              <td className="WPSleft-td-nobottomnoleft" style={{ textAlign: "center" }}>
                {pdf[i]?.WPSD_FM_Classification || "--"}
              </td>
              <td className="WPSleft-td-nobottomnoleft" style={{ textAlign: "center" }}>
                {pdf[i]?.WPSD_FM_FNo}
              </td>
              <td className="WPSleft-td-nobottomnoleft" style={{ textAlign: "center" }}>
                {pdf[i]?.WPSD_FM_Ano}
              </td>
              <td className="WPSleft-td-nobottomnoleft" style={{ textAlign: "center" }}>
                {pdf[i]?.WPSD_FM_ChemicalAnalysis_TradeName || "--"}
              </td>
            </tr>
          );
        } else {
          rows.push(
            <tr key={i}>
              <th colSpan="2" className="WPSleft-th-nobottom">
                {pdf[i]?.WPSD_WP_WeldingProcess}
              </th>
              <td className="WPSleft-td-nobottom">
                {pdf[i]?.WPSD_FM_SFA}
              </td>
              <td className="WPSleft-td-nobottom" style={{ borderLeft: "none" }}>
                {pdf[i]?.WPSD_FM_Classification || "--"}
              </td>

              {pdfData.wld_WPSType === "Overlay" && (
                <td className="WPSleft-td-nobottom" style={{ borderLeft: "none" }}>
                  {pdf[i]?.WPSH_WeldLayer}
                </td>
              )}
              <td className="WPSleft-td-nobottom" style={{ borderLeft: "none" }}>
                {pdf[i]?.WPSD_FM_FNo}
              </td>
              <td className="WPSleft-td-nobottom" style={{ borderLeft: "none" }}>
                {pdf[i]?.WPSD_FM_Ano || "--"}
              </td>
              <td className="WPSleft-td-nobottom" style={{ borderLeft: "none" }}>
                {pdf[i]?.WPSD_FM_ChemicalAnalysis_TradeName || "--"}
              </td>
              <td className="WPSleft-td-nobottom" style={{ borderLeft: "none", textAlign: "center" }}>
                {pdf[i]?.WPSD_FM_TRQ_Min || "--"}
              </td>
              <td className="WPSleft-td-nobottom" style={{ borderLeft: "none", textAlign: "center" }}>
                {pdf[i]?.WPSD_FM_TRQ_Max || "--"}
              </td>
            </tr>
          );
        }
      }
    }

    return rows;
  };

  const renderStandardTable = () => (

    <table id="table" className="WPSTable6">
      <tbody>
        <tr>
          <th colSpan="2" rowSpan="2" className="WPSnoborder widthth3table" style={{ borderBottom: ".1px solid black", backgroundColor: "white" }}></th>
          <th className="WPSleft-th" rowSpan="2" style={{ width: "8%", borderLeft: ".1px solid black" }}>SFA</th>
          <th className="WPSleft-th" rowSpan="2" style={{ width: "16%", borderRight: ".1px solid black" }}>Classification</th>

          {pdfData?.wld_WPSType === "Overlay" && (
            <th className="WPSleft-th" rowSpan="2" style={{ width: "12%" }}>Weld Layer</th>
          )}

          <th className="WPSleft-th" rowSpan="2" style={{ width: "4.5%" }}>F-no</th>
          <th className="WPSleft-th" rowSpan="2" style={{ width: "4.5%" }}>A-no</th>
          <th className="WPSleft-th" rowSpan="2" style={{ width: "35%" }}>Chemical Analysis or Trade name</th>

          <th className="WPSleft-th-nobottom" colSpan="2" style={{ width: "30%", textAlign: "center", borderLeft: pdfData?.wld_TRQ_Type === "Welded" ? "none" : "0px solid black", borderRight: "1px solid black", borderTop: "1px solid black" }}>
            {pdfData?.wld_TRQ_Type === "Welded" ? "As-Welded" : "With PWHT"}
          </th>
        </tr>

        <tr>
          <th className="WPSleft-th-nobottomnoright" style={{ textAlign: "center", borderLeft: pdfData?.wld_TRQ_Type === "Welded" ? "none" : "1px solid black" }}>Min</th>
          <th className="WPSleft-th-notopnoleft" style={{ textAlign: "center", borderRight: "1px solid black" }}>Max</th>
        </tr>

        {renderProcessRows()}
        {(() => {


          return FillerMetal.map((item, index) => {
            const isFirst = index === 0;

            return (
              <tr key={`${item.prefix}-${item.type}`}>
                <th colSpan="2" className="WPSleft-th-nobottomflux" style={{
                  borderRight: "1px solid black",
                  borderTop: isFirst ? "1px solid black" : "none",
                  borderBottom: "none"
                }}>{item.type}</th>
                <td className="WPSleft-td-nobottomtop" style={{
                  borderLeft: "none",
                  borderTop: isFirst ? "1px solid black" : "none",
                  borderBottom: "none"
                }}>
                  {pdfData?.[`${item.prefix}_SFA`] || "--"}
                </td>
                <td className="WPSleft-td-nobottomtop" style={{
                  borderLeft: "none",
                  borderTop: isFirst ? "1px solid black" : "none",
                  borderBottom: "none"
                }}>
                  {pdfData?.[`${item.prefix}_Classification`] || "--"}
                </td>

                {pdfData.wld_WPSType === "Overlay" && (
                  <td className="WPSleft-td-nobottomtop" style={{
                    borderLeft: "none",
                    borderTop: isFirst ? "1px solid black" : "none",
                    borderBottom: "none"
                  }}>--</td>
                )}

                <td className="WPSleft-td-nobottomtop" style={{
                  borderLeft: "none",
                  borderTop: isFirst ? "1px solid black" : "none",
                  borderBottom: "none"
                }}>
                  {pdfData?.[`${item.prefix}_FNo`] || "--"}
                </td>
                <td className="WPSleft-td-nobottomtop" style={{
                  borderLeft: "none",
                  borderTop: isFirst ? "1px solid black" : "none",
                  borderBottom: "none"
                }}>
                  {pdfData?.[`${item.prefix}_ANo`] || "--"}
                </td>
                <td className="WPSleft-td-nobottomtop" style={{
                  borderLeft: "none",
                  borderTop: isFirst ? "1px solid black" : "none",
                  borderBottom: "none"
                }}>
                  {pdfData?.[`${item.prefix}_ChemicalAnalysis_TradeName`] || "--"}
                </td>
                <td className="WPSleft-td-nobottomtop" style={{
                  borderLeft: "none",
                  borderTop: isFirst ? "1px solid black" : "none",
                  borderBottom: "none"
                }}>--</td>
                <td className="WPSleft-td-nobottomtop" style={{
                  borderLeft: "none",
                  borderTop: isFirst ? "1px solid black" : "none",
                  borderBottom: "none"
                }}>--</td>
              </tr>
            );
          });
        })()}

        {(() => {
          const isOverlay = pdf[0]?.wld_WPSType === "Overlay";
          const rows = isOverlay ? [
            { left: "Flux Type", leftField: "WPSH_FM_FluxType", right: "Flux from recrush slag", rightField: "WPSH_FM_FluxFromRecrush" },
            { left: "Alloy Element", leftField: "WPSH_FM_AlloyElement", right: "Add/Del or Chg > 10% in suppl. FM", rightField: "WPSH_FM_Add_Del_orchange" },
            { left: "Nominal Flux", leftField: "WPSH_FM_NominalFlux", right: "Filler Metal Others", rightField: "WPSH_FM_FilerMetal", isLast: true }
          ] : [
            { left: "Flux Type", leftField: "WPSH_FM_FluxType", right: "Flux from recrush slag", rightField: "WPSH_FM_FluxFromRecrush" },
            { left: "Alloy Flux", leftField: "WPSH_FM_AlloyFlux", right: "Alloy Element", rightField: "WPSH_FM_AlloyElement" },
            { left: "Add. of Filler", leftField: "WPSH_FM_Add_Del_Filler", right: "Change in electrode dia.", rightField: "WPSH_Tech_Change_Electrode_dia" },
            { left: "Add. of Flux (GTAW)", leftField: "WPSH_FM_Add_Del_Flux", right: "Filler Metal others", rightField: "WPSH_FM_FilerMetal", isLast: true }
          ];

          return rows.map((row, index) => (
            <tr key={`${row.leftField}-${row.rightField}`}>
              <th className="WPSleft-th" colSpan={isOverlay ? "2" : "3"} style={{
                width: "44px", borderRight: "none", borderLeft: "1px solid black",
                borderBottom: row.isLast ? "1px solid black" : "none"
              }}>
                {row.left}
              </th>
              <td className="WPSleft-td" colSpan={isOverlay ? "5" : "3"} style={{
                borderBottom: row.isLast ? "1px solid black" : "none"
              }}>
                {pdf[0]?.[row.leftField]}
              </td>
              <th className="WPSleft-th" style={{
                borderBottom: row.isLast ? "1px solid black" : "none"
              }}>
                {row.right}
              </th>
              <td className="WPSleft-td" colSpan="4" style={{
                borderLeft: "none", borderBottom: row.isLast ? "1px solid black" : "none"
              }}>
                {pdf[0]?.[row.rightField]}
              </td>
            </tr>
          ));
        })()}
      </tbody>
    </table>

  );

  const renderTubesheetTable = () => (
    <div>
      <table id="table" className="WPSTable6" style={{ width: "72%" }}>
        <tbody>
          <tr>
            <th colSpan="2" rowSpan="1" style={{ width: "21.8%", background: "white", borderRight: "none" }}></th>
            <th className="WPSleft-th" rowSpan="1" style={{ width: "15%", borderLeft: "1px solid black", textAlign: "center" }}>SFA</th>
            <th className="WPSleft-th" rowSpan="1" style={{ width: "27%", textAlign: "center" }}>Classification</th>
            <th className="WPSleft-th" rowSpan="1" style={{ width: "15%", textAlign: "center" }}>F-no</th>
            <th className="WPSleft-th" rowSpan="1" style={{ width: "15%", textAlign: "center" }}>A-no</th>
            <th className="WPSleft-th" rowSpan="1" style={{ width: "28%", textAlign: "center" }}>Chemical Analysis or Trade name</th>
          </tr>
          {renderProcessRows()}
        </tbody>
      </table>

      <table id="table" className="WPSTable8" style={{ width: "15.1cm", top: "0px", position: "relative", marginTop: "0px" }}>
        <tbody>
          {[
            { label: "Preplaced filler metal", field: "WPSH_FM_PreFillerMetal" },
            { label: "With/without filler metal", field: "WPSH_FM_WithFillerMetal" },
            { label: "Filler metal Product form", field: "WPSH_FM_FillerProduct" },
            { label: "Inserts", field: "WPSH_FM_Inserts" },
            { label: "Filler Metals - Flux", field: "WPSH_FM_FillerFlux" }
          ].map((item, index) => (
            <tr key={item.field} style={index === 0 ? { borderTop: "none" } : {}}>
              <th style={(() => {
                if (index === 0) {
                  return { width: "18%", paddingTop: "0px", marginTop: "0px" };
                }
                if (index === 5) {
                  return { borderBottom: "1px solid black" };
                }
                return {};
              })()}>
                {item.label}
              </th>
              <td className="WPSleft-td-nobottomnoleft" style={(() => {
                if (index === 0) {
                  return { borderTop: "0", paddingLeft: "3px" };
                }
                if (index === 5) {
                  return { borderBottom: "1px solid black", paddingLeft: "3px" };
                }
                return { paddingLeft: "3px" };
              })()}>
                {pdfData?.[item.field] || "--"}
              </td>
            </tr>
          ))}

          <tr>
            <th style={{ borderBottom: "1px solid black" }}>Filler Metals - Others</th>
            <td className="WPSleft-td-nobottomnoleft" style={{ borderBottom: "1px solid black", paddingLeft: "3px" }}>
              {pdfData?.WPSH_FM_FillerOthers || "--"}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  return isTubesheet ? renderTubesheetTable() : renderStandardTable();
};

export default FillerMetalTable;